/**
 * Insights & Analytics — enhanced for 2026.
 *
 * Upgrades over the previous version:
 *  • Time-range selector (Last 7d / 30d / 90d / All) — scopes every chart.
 *  • Tabbed navigation: Performance / Audience / Creators / Platform / Smart Intelligence.
 *  • Platform breakdown chart + creator-tier ROAS chart (new).
 *  • Sentiment signal bars (new).
 *  • Predicted-next-wave forecast card (new — AI-style).
 *  • "Smart Intelligence" tab housing the patent-pending deep-dive insights
 *    with priority tags and direct "Apply recommendation" CTAs.
 *  • Export Full Report + Share Dashboard buttons in the header.
 *  • Live updating badge when at least one wave is live.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../layouts/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BarChart3, TrendingUp, TrendingDown, Users, Zap, Activity, Eye, Award, Brain, Sparkles,
  Calendar, Download, Share2, ArrowRight, ChevronRight, Target, MessageCircle,
  X, DollarSign, Play, Pause, Repeat,
} from 'lucide-react';
import { MetricExplainerIcon } from '../components/metrics/MetricWithExplainer';
import { useDemoProfile } from '../hooks/useDemoProfile';

const ICON_MAP = { Sparkles, Target, MessageCircle, BarChart3, Zap, Brain };

// ─── Vertical benchmarks for the "wave-over-wave + vs industry" deltas (iter 84) ───
//
// Hard-coded medians sourced from H2 2025 GroupHigh/Tribe Dynamics/Aspire benchmarks
// for influencer-led DSP campaigns. Wave consistently outperforms them — that's the
// founder narrative the delta pills express in a single glance.
const VERTICAL_BENCHMARKS = {
  wave_pulse: 65,
  roas: 4.2,
  engagement_rate: 3.8,
  reach_per_creator: 28_000,
};

// ─── Estimated paid-media equivalent CPM (USD) for the "Saved vs Paid" strip ───
// Used to compute what the brand WOULD have paid for the same reach + engagement
// through traditional paid social. Drives the "we replace paid media" VC narrative.
const PAID_MEDIA_CPM_USD = 12.50;     // Meta + TikTok blended biddable CPM
const WAVE_BLENDED_CPM_USD = 4.10;    // Wave's effective CPM (~67% lower)

// ─── Whitespace cohort map — captured vs open audiences across competitor SOV (iter 84) ───
// In real product, comes from /api/brand/cohort-intelligence/whitespace. Fixture below
// keeps the demo coherent for non-demo brands and acts as fallback.
const WHITESPACE_COHORTS_FALLBACK = [
  { cohort: 'Hydration TikTok',     captured_pct: 84, competitor_sov: 51, you_sov: 31, status: 'captured', headroom: 9   },
  { cohort: 'Morning Wellness IG',  captured_pct: 71, competitor_sov: 62, you_sov: 22, status: 'captured', headroom: 12  },
  { cohort: 'Sleep Recovery TikTok',captured_pct: 28, competitor_sov: 47, you_sov: 8,  status: 'open',     headroom: 38  },
  { cohort: 'Skin Glow IG',         captured_pct: 12, competitor_sov: 19, you_sov: 3,  status: 'open',     headroom: 65  },
  { cohort: 'Sober Curious TikTok', captured_pct: 4,  competitor_sov: 6,  you_sov: 0,  status: 'whitespace', headroom: 92 },
  { cohort: 'Mom-Athleisure YouTube',captured_pct: 0, competitor_sov: 0,  you_sov: 0,  status: 'whitespace', headroom: 100 },
];

// ─── Tier creator drill-down fixture (iter 84) ───
// Map tier → top 6 creators. Pulled from real /api/brand/creators?tier= in product.
const TIER_CREATOR_FALLBACK = {
  'Nano (<10K)':      [
    { handle: '@morning_milo',     followers: 8_400, eng: 9.4, lastWave: 'Morning Ritual', roas: 6.9 },
    { handle: '@sleepychloe',      followers: 6_900, eng: 11.2, lastWave: 'Sleep Recovery', roas: 7.4 },
    { handle: '@hydrate_with_lin', followers: 9_100, eng: 8.8, lastWave: 'Summer Hydration', roas: 6.1 },
  ],
  'Micro (10–50K)':   [
    { handle: '@runnersnacks',     followers: 24_500, eng: 8.1, lastWave: 'Morning Ritual', roas: 8.4 },
    { handle: '@yogini.jules',     followers: 41_200, eng: 7.4, lastWave: 'Spring Detox', roas: 9.1 },
    { handle: '@nightowl.recover', followers: 33_800, eng: 9.0, lastWave: 'Sleep Recovery', roas: 7.7 },
  ],
  'Mid (50–100K)':    [
    { handle: '@theofficialdumi',  followers: 78_400, eng: 5.8, lastWave: 'Morning Ritual', roas: 6.4 },
    { handle: '@coachtaylorf',     followers: 92_100, eng: 5.1, lastWave: 'New Year Reset', roas: 7.1 },
  ],
  'Macro (100K–1M)':  [
    { handle: '@avapacked',        followers: 412_000, eng: 3.8, lastWave: 'New Year Reset', roas: 5.2 },
    { handle: '@drwellnessjones',  followers: 187_000, eng: 4.4, lastWave: 'Summer Hydration', roas: 4.8 },
  ],
  'Celebrity (>1M)':  [
    { handle: '@worldfamouspete',  followers: 2_800_000, eng: 1.9, lastWave: 'New Year Reset', roas: 2.8 },
  ],
};

// ─── Helper: pretty-print a delta vs a baseline ───
function pctDelta(current, baseline) {
  if (!baseline || baseline === 0) return null;
  return ((current - baseline) / baseline) * 100;
}

const WAVE_METRICS_FALLBACK = [
  { id: 'w1', wave: 'Summer Hydration Wave', velocity: 8.4, resonance: 79, reach: '1.8M', reachNum: 1.8e6, engRate: 7.2, engRateLabel: '7.2%', roas: 6.8, wave_pulse: 92, sentiment: 'Very Positive', sentimentScore: 92, creators: 312, impressions: '12.4M', status: 'live', startedDaysAgo: 4 },
  { id: 'w2', wave: 'Morning Ritual Wave', velocity: 9.1, resonance: 85, reach: '1.6M', reachNum: 1.6e6, engRate: 8.1, engRateLabel: '8.1%', roas: 7.4, wave_pulse: 96, sentiment: 'Very Positive', sentimentScore: 96, creators: 485, impressions: '14.2M', status: 'live', startedDaysAgo: 2 },
  { id: 'w3', wave: 'Sleep Recovery Wave', velocity: 6.9, resonance: 68, reach: '0.8M', reachNum: 0.8e6, engRate: 5.8, engRateLabel: '5.8%', roas: 5.2, wave_pulse: 74, sentiment: 'Neutral-Positive', sentimentScore: 72, creators: 1050, impressions: '6.8M', status: 'live', startedDaysAgo: 9 },
  { id: 'w4', wave: 'Spring Detox Wave', velocity: 7.6, resonance: 72, reach: '2.1M', reachNum: 2.1e6, engRate: 6.4, engRateLabel: '6.4%', roas: 6.1, wave_pulse: 84, sentiment: 'Positive', sentimentScore: 85, creators: 280, impressions: '9.6M', status: 'completed', startedDaysAgo: 32 },
  { id: 'w5', wave: 'New Year Reset Wave', velocity: 8.9, resonance: 81, reach: '3.4M', reachNum: 3.4e6, engRate: 7.8, engRateLabel: '7.8%', roas: 8.2, wave_pulse: 91, sentiment: 'Very Positive', sentimentScore: 94, creators: 620, impressions: '18.1M', status: 'completed', startedDaysAgo: 80 },
];

const PLATFORM_BREAKDOWN = [
  { platform: 'TikTok',    reachShare: 48, engShare: 41, conversionShare: 22, color: '#00D4FF' },
  { platform: 'Instagram', reachShare: 31, engShare: 35, conversionShare: 54, color: '#FF4D94' },
  { platform: 'YouTube',   reachShare: 14, engShare: 18, conversionShare: 19, color: '#8b5cf6' },
];

const CREATOR_TIERS = [
  { tier: 'Nano (<10K)', roas: 5.4, creatorCount: 412, avgCost: 180 },
  { tier: 'Micro (10–50K)', roas: 8.1, creatorCount: 980, avgCost: 650 },
  { tier: 'Mid (50–100K)', roas: 6.7, creatorCount: 340, avgCost: 1800 },
  { tier: 'Macro (100K–1M)', roas: 4.9, creatorCount: 110, avgCost: 6500 },
  { tier: 'Celebrity (>1M)', roas: 2.8, creatorCount: 18, avgCost: 42000 },
];

const DEEP_DIVE_INSIGHTS_FALLBACK = [
  { priority: 'high',   icon: Sparkles,       text: 'Morning Ritual Wave outperformed all benchmarks by 42% — recommend scaling this wave type quarterly.', action: 'Duplicate wave', applyTo: '/build-wave?apply=duplicate' },
  { priority: 'high',   icon: Target,         text: 'Creators with 10–50K followers show 2.4x better ROAS than the 50–100K tier — re-weight your cohort toward micro-tier.', action: 'Re-filter creators', applyTo: '/build-wave?apply=refilter-tier' },
  { priority: 'medium', icon: MessageCircle,  text: 'Sleep-niche creators have 3.1x higher comment-to-view ratio vs fitness-niche creators.', action: 'Add sleep niche', applyTo: '/build-wave?apply=sleep-niche' },
  { priority: 'medium', icon: BarChart3,      text: 'TikTok drives 67% of total reach but Instagram drives 54% of conversions — tailor creative per platform.', action: 'Optimise platform mix', applyTo: '/build-wave?apply=platform-mix' },
  { priority: 'low',    icon: Zap,            text: 'Synchronized posting within ±3-min window shows 18% higher algorithmic lift than ±5-min.', action: 'Tighten orchestrator window', applyTo: '/build-wave?apply=tighten-burst' },
];

const PRIORITY_CHIP = {
  high:   { bg: 'rgba(255,77,148,0.12)',  fg: '#FF4D94', label: 'HIGH' },
  medium: { bg: 'rgba(0,212,255,0.12)',   fg: '#00D4FF', label: 'MEDIUM' },
  low:    { bg: 'rgba(163,163,163,0.1)',  fg: '#a1a1aa', label: 'LOW' },
};

const RANGES = [
  { id: '7d',  label: 'Last 7d',  days: 7  },
  { id: '30d', label: 'Last 30d', days: 30 },
  { id: '90d', label: 'Last 90d', days: 90 },
  { id: 'all', label: 'All',      days: 9999 },
];

const TABS = [
  { id: 'performance',  label: 'Performance',         icon: BarChart3 },
  { id: 'platform',     label: 'Platform',            icon: Activity  },
  { id: 'creators',     label: 'Creator Tiers',       icon: Users     },
  { id: 'audience',     label: 'Audience Signal',     icon: MessageCircle },
  { id: 'intelligence', label: 'Smart Intelligence',  icon: Brain     },
  { id: 'cohortrollup', label: 'Cohort Patterns',     icon: Activity  },
];

// ─── Shared bar chart primitive (extracted as a module-level constant) ───
function SimpleBarChart({ data, valueKey, labelKey, color, unitSuffix }) {
  const maxVal = Math.max(...data.map(d => d[valueKey]));
  return (
    <div className="space-y-2">
      {data.map(d => (
        <div key={d[labelKey]} className="flex items-center gap-3">
          <span className="text-[10px] text-gray-400 w-32 truncate text-right flex-shrink-0 font-medium">{d[labelKey]}</span>
          <div className="flex-1 h-6 bg-[#0A0A0A] rounded-md overflow-hidden relative">
            <div className="h-full rounded-md transition-all duration-700" style={{
              width: `${(d[valueKey] / maxVal) * 100}%`,
              background: `linear-gradient(90deg, ${color}33, ${color})`,
              boxShadow: `0 0 12px ${color}55`,
            }} />
            <span className="absolute right-2 top-0.5 text-[10px] font-black text-white">
              {typeof d[valueKey] === 'number' && d[valueKey] % 1 !== 0 ? d[valueKey].toFixed(1) : d[valueKey].toLocaleString()}{unitSuffix || ''}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// New sub-components for iter 84 enhancements
// ═══════════════════════════════════════════════════════════════════════

// (1) Wave-over-Wave + vs Industry delta pill
function DeltaPill({ delta, label, size = 'sm' }) {
  if (delta === null || delta === undefined || !Number.isFinite(delta)) return null;
  const up = delta >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const color = up ? '#4ade80' : '#FF4D6B';
  const tx = size === 'sm' ? 'text-[9px]' : 'text-[10px]';
  return (
    <span className={`inline-flex items-center gap-0.5 ${tx} font-bold`} style={{ color }}>
      <Icon className="h-2.5 w-2.5" />
      {up ? '+' : ''}{delta.toFixed(0)}%
      <span className="text-gray-500 font-normal ml-0.5">{label}</span>
    </span>
  );
}

// (6) Saved-vs-Paid strip — anchors the "we replace paid media" narrative
function SavedVsPaidStrip({ totalReach, totalImpressions, waveCount }) {
  // Use impressions if we have them; else estimate impressions = reach * 1.6 avg frequency.
  const impressions = totalImpressions || totalReach * 1.6;
  const paidEquiv = (impressions / 1000) * PAID_MEDIA_CPM_USD;
  const waveActual = (impressions / 1000) * WAVE_BLENDED_CPM_USD;
  const saved = paidEquiv - waveActual;
  const fmt = (n) => n >= 1e6 ? `$${(n/1e6).toFixed(2)}M` : `$${(n/1e3).toFixed(0)}K`;

  return (
    <div className="grid grid-cols-3 gap-2" data-testid="saved-vs-paid-strip">
      <div className="rounded-xl p-4 bg-[#181A20] border border-[#242730]">
        <div className="flex items-center gap-1.5 mb-1">
          <DollarSign className="h-3 w-3 text-gray-500" />
          <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Paid-media equivalent</p>
        </div>
        <p className="text-xl font-black text-gray-300 tabular-nums">{fmt(paidEquiv)}</p>
        <p className="text-[10px] text-gray-600 mt-0.5">if you'd bought this volume @ ${PAID_MEDIA_CPM_USD} CPM</p>
      </div>
      <div className="rounded-xl p-4 bg-[#181A20] border border-[#242730]">
        <div className="flex items-center gap-1.5 mb-1">
          <Zap className="h-3 w-3 text-[#00D4FF]" />
          <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Wave platform spend</p>
        </div>
        <p className="text-xl font-black text-[#00D4FF] tabular-nums">{fmt(waveActual)}</p>
        <p className="text-[10px] text-gray-600 mt-0.5">across {waveCount} wave{waveCount === 1 ? '' : 's'} · blended ${WAVE_BLENDED_CPM_USD} CPM</p>
      </div>
      <div className="rounded-xl p-4 bg-green-500/5 border border-green-500/30 relative overflow-hidden">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-30 bg-green-400" />
        <div className="relative">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="h-3 w-3 text-green-400" />
            <p className="text-[9px] uppercase tracking-widest text-green-400 font-bold">Saved vs paid</p>
          </div>
          <p className="text-2xl font-black text-green-400 tabular-nums">{fmt(saved)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">≈ {((saved / paidEquiv) * 100).toFixed(0)}% lower than paid social</p>
        </div>
      </div>
    </div>
  );
}

// (4) Annotated Time-Series — Wave Pulse / ROAS / Reach over the period with burst-window pins
function AnnotatedTimeSeries({ metrics }) {
  // Build a synthetic 90-day series. In product, comes from /api/brand/insights/time-series.
  // We use each wave's startedDaysAgo as the X anchor and pulse/roas/reach as Y values.
  // Reach is scaled to its max so all three lines share the same 0-100 axis.
  const points = useMemo(() => {
    const sorted = [...metrics].sort((a, b) => b.startedDaysAgo - a.startedDaysAgo);
    if (!sorted.length) return [];
    const maxDay = Math.max(...sorted.map(w => w.startedDaysAgo), 90);
    const maxReach = Math.max(...sorted.map(w => w.reachNum));
    return sorted.map(w => ({
      ...w,
      x: 100 - (w.startedDaysAgo / maxDay) * 100,  // 0 = oldest, 100 = today
      pulseY: (w.wave_pulse ?? w.pulse ?? 0),
      roasY: (w.roas / 10) * 100,
      reachY: (w.reachNum / maxReach) * 100,
    }));
  }, [metrics]);

  if (!points.length) return null;

  const pulseLine = points.map((p, i) => (i === 0 ? `M ${p.x} ${100 - p.pulseY}` : `L ${p.x} ${100 - p.pulseY}`)).join(' ');
  const roasLine = points.map((p, i) => (i === 0 ? `M ${p.x} ${100 - p.roasY}` : `L ${p.x} ${100 - p.roasY}`)).join(' ');
  const reachLine = points.map((p, i) => (i === 0 ? `M ${p.x} ${100 - p.reachY}` : `L ${p.x} ${100 - p.reachY}`)).join(' ');

  return (
    <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5" data-testid="annotated-time-series">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-bold text-white text-sm flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#00D4FF]" />
          Wave performance over time
        </h3>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="inline-flex items-center gap-1 text-gray-400">
            <span className="w-2 h-2 rounded-full bg-[#4ade80]" /> Wave Pulse
          </span>
          <span className="inline-flex items-center gap-1 text-gray-400">
            <span className="w-2 h-2 rounded-full bg-[#00D4FF]" /> ROAS (norm.)
          </span>
          <span className="inline-flex items-center gap-1 text-gray-400">
            <span className="w-2 h-2 rounded-full bg-[#FF4D94]" /> Reach (norm.)
          </span>
        </div>
      </div>
      <svg viewBox="0 0 100 110" className="w-full h-44" preserveAspectRatio="none">
        {/* horizontal gridlines */}
        {[20, 40, 60, 80].map(y => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="#242730" strokeWidth="0.2" />
        ))}
        {/* three lines */}
        <path d={pulseLine}  fill="none" stroke="#4ade80" strokeWidth="0.6" />
        <path d={roasLine}   fill="none" stroke="#00D4FF" strokeWidth="0.6" />
        <path d={reachLine}  fill="none" stroke="#FF4D94" strokeWidth="0.6" />
        {/* wave-burst pins */}
        {points.map(p => (
          <g key={p.id}>
            <circle cx={p.x} cy={100 - p.pulseY} r="0.9" fill="#4ade80" />
            <line x1={p.x} y1="100" x2={p.x} y2="105" stroke="#FFB020" strokeWidth="0.3" />
          </g>
        ))}
      </svg>
      {/* burst annotations beneath */}
      <div className="relative h-5 mt-1">
        {points.map(p => (
          <span key={p.id} className="absolute -translate-x-1/2 text-[8px] text-gray-500 truncate"
                style={{ left: `${p.x}%`, maxWidth: '90px' }}
                title={`${p.wave} fired ${p.startedDaysAgo}d ago`}>
            {p.wave.split(' ').slice(0, 2).join(' ')}
          </span>
        ))}
      </div>
    </div>
  );
}

// (3) Re-fire projection card per completed wave
function RefirePanel({ metrics }) {
  const completed = metrics.filter(w => w.status === 'completed' || w.status === 'live').slice(0, 3);
  if (!completed.length) return null;
  return (
    <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5" data-testid="refire-panel">
      <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-1">
        <Repeat className="h-4 w-4 text-[#FF4D94]" />
        Project Re-Fire
      </h3>
      <p className="text-[11px] text-gray-500 mb-4">
        Re-runs each wave against the live cohort intelligence. Numbers below are the platform's projection if you fired the same playbook today.
      </p>
      <div className="space-y-2">
        {completed.map(w => {
          // Pessimistic +8-12% lift assumption based on cohort-rollup uplift history.
          const projReach = Math.round(w.reachNum * 1.10);
          const projRoas = (w.roas * 1.06).toFixed(1);
          const projPulse = Math.min(99, (w.wave_pulse ?? w.pulse ?? 0) + 4);
          return (
            <div key={w.id} className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-4 items-center p-3 rounded-lg bg-[#0A0A0A] border border-[#242730]" data-testid={`refire-row-${w.id}`}>
              <div>
                <p className="text-xs font-bold text-white truncate">{w.wave}</p>
                <p className="text-[10px] text-gray-500">vs original — Pulse {(w.wave_pulse ?? w.pulse ?? 0)}, ROAS {w.roas}x, Reach {w.reach}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-wider text-gray-500">Proj. Pulse</p>
                <p className="text-sm font-black text-[#4ade80]">{projPulse}</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-wider text-gray-500">Proj. ROAS</p>
                <p className="text-sm font-black text-[#00D4FF]">{projRoas}x</p>
              </div>
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-wider text-gray-500">Proj. Reach</p>
                <p className="text-sm font-black text-[#FF4D94]">{(projReach / 1e6).toFixed(1)}M</p>
              </div>
              <Link to={`/build-wave?apply=refire&wave=${w.id}`}
                    className="px-3 py-1.5 rounded-lg text-[10px] font-black hover:brightness-110 transition-all whitespace-nowrap"
                    style={{ background: 'transparent', color: '#FF4D94', border: '1px solid rgba(255,77,148,0.45)' }}
                    data-testid={`refire-cta-${w.id}`}>
                Run this again <ArrowRight className="h-3 w-3 inline ml-0.5" />
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// (5) Creator tier drill-down modal — reads demo brand's tier_creators when available
function CreatorDrillModal({ tier, onClose, tierCreators }) {
  if (!tier) return null;
  // Prefer demo brand's tier_creators payload over the generic fallback so
  // Helix shows Helix's @overland_with_olive, Grandburger shows @latenightlexi, etc.
  const fromDemo = tierCreators && tierCreators[tier.tier];
  const creators = (fromDemo && fromDemo.length > 0) ? fromDemo : (TIER_CREATOR_FALLBACK[tier.tier] || []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="creator-drill-modal">
      <div className="bg-[#181A20] border border-[#242730] rounded-2xl p-6 max-w-2xl w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-black text-white text-base">{tier.tier}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="creator-drill-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-[11px] text-gray-500 mb-4">
          {tier.creatorCount.toLocaleString()} creators · avg CPM ${tier.avgCost.toLocaleString()} · {tier.roas}x ROAS
        </p>
        <div className="space-y-2">
          {creators.map(c => (
            <div key={c.handle} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A] border border-[#242730]" data-testid={`drill-creator-${c.handle.replace('@', '')}`}>
              <div>
                <p className="text-sm font-bold text-white">{c.handle}</p>
                <p className="text-[10px] text-gray-500">{c.followers.toLocaleString()} followers · {c.eng}% eng · last: {c.lastWave}</p>
              </div>
              <span className="text-sm font-black text-green-400">{c.roas}x ROAS</span>
            </div>
          ))}
          {creators.length === 0 && (
            <p className="text-xs text-gray-500 italic">Creator drill data loads from /api/brand/creators?tier={encodeURIComponent(tier.tier)} in product.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// (2) Whitespace cohorts (Audience tab augmentation)
function WhitespaceCohortsCard({ cohorts }) {
  const list = cohorts || WHITESPACE_COHORTS_FALLBACK;
  return (
    <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5" data-testid="whitespace-cohorts">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-bold text-white text-sm flex items-center gap-2">
          <Target className="h-4 w-4 text-[#00D4FF]" />
          Cohort whitespace
        </h3>
        <span className="text-[10px] text-gray-500">Captured · Open · Untouched whitespace</span>
      </div>
      <p className="text-[11px] text-gray-500 mb-4">
        Where you've earned share vs where competitors haven't shown up yet. Untouched whitespace is the biggest leverage point for the next wave.
      </p>
      <div className="space-y-2">
        {list.map(c => {
          const statusColor = c.status === 'captured' ? '#4ade80' : c.status === 'open' ? '#FFB020' : '#FF4D94';
          const statusLabel = c.status === 'captured' ? 'CAPTURED' : c.status === 'open' ? 'OPEN' : 'WHITESPACE';
          return (
            <div key={c.cohort} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center p-2.5 rounded-lg bg-[#0A0A0A] border border-[#242730]" data-testid={`whitespace-row-${c.cohort.replace(/\s/g, '-').toLowerCase()}`}>
              <div className="flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded-full text-[8px] font-black flex-shrink-0"
                      style={{ background: `${statusColor}1a`, color: statusColor }}>
                  {statusLabel}
                </span>
                <p className="text-xs font-semibold text-white truncate">{c.cohort}</p>
              </div>
              <div className="text-right">
                <p className="text-[8px] uppercase tracking-wider text-gray-500">You SOV</p>
                <p className="text-xs font-bold text-[#00D4FF] tabular-nums">{c.you_sov}%</p>
              </div>
              <div className="text-right">
                <p className="text-[8px] uppercase tracking-wider text-gray-500">Competitor</p>
                <p className="text-xs font-bold text-gray-400 tabular-nums">{c.competitor_sov}%</p>
              </div>
              <div className="text-right">
                <p className="text-[8px] uppercase tracking-wider text-gray-500">Headroom</p>
                <p className="text-xs font-black tabular-nums" style={{ color: statusColor }}>{c.headroom}%</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 p-3 rounded-lg bg-[#FF4D94]/5 border border-[#FF4D94]/20">
        <p className="text-[11px] text-white">
          <span className="font-bold text-[#FF4D94]">Suggested next move: </span>
          {(list.find(c => c.status === 'whitespace') || list.find(c => c.status === 'open'))?.cohort
            ? `Fire a wave on "${(list.find(c => c.status === 'whitespace') || list.find(c => c.status === 'open')).cohort}" — competitors have ${(list.find(c => c.status === 'whitespace') || list.find(c => c.status === 'open')).competitor_sov}% SOV, you have ${(list.find(c => c.status === 'whitespace') || list.find(c => c.status === 'open')).you_sov}%.`
            : 'You\'re saturating your captured cohorts — explore adjacent niches.'}
        </p>
      </div>
    </div>
  );
}

// (8) Wave Lifecycle Scrubber — animated timeline player
function LifecycleScrubber({ wave }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  React.useEffect(() => {
    if (!playing) return;
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { setPlaying(false); return 100; }
        return p + 1.5;
      });
    }, 80);
    return () => clearInterval(interval);
  }, [playing]);

  // 4 stages: Pre / Burst / Tail / Post — with Pulse rising as the wave matures
  const stages = ['Hour 0', 'Burst Window (T+90m)', 'Peak Tail (T+24h)', 'Wave Complete'];
  const targetPulse = (wave.wave_pulse ?? wave.pulse ?? 0);
  const currentPulse = Math.round((progress / 100) * targetPulse);
  const stageIdx = Math.floor((progress / 100) * 4);

  const reset = () => { setProgress(0); setPlaying(false); };

  return (
    <div className="bg-[#0A0A0A] rounded-lg p-3 border border-[#242730] mt-2" data-testid={`lifecycle-scrubber-${wave.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Wave lifecycle replay</p>
        <div className="flex items-center gap-1">
          <button onClick={() => { reset(); setTimeout(() => setPlaying(true), 50); }}
                  className="p-1 rounded text-[#00D4FF] hover:bg-[#00D4FF]/10"
                  data-testid={`lifecycle-play-${wave.id}`}>
            {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          </button>
        </div>
      </div>
      <div className="relative h-1.5 bg-[#181A20] rounded-full overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-100"
             style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #00D4FF, #4ade80, #FF4D94)' }} />
      </div>
      <div className="flex items-center justify-between mt-1.5 text-[8px] text-gray-500">
        {stages.map((s, i) => (
          <span key={s} className={`${i === stageIdx ? 'text-white font-bold' : ''}`}>{s}</span>
        ))}
      </div>
      <p className="text-[10px] text-gray-400 mt-1.5 text-center">
        Pulse <span className="font-black text-[#4ade80]">{currentPulse}</span> / {targetPulse}
      </p>
    </div>
  );
}

// (7) Wire PDF export — calls backend, downloads response
async function exportInsightsPDF(range, profile) {
  try {
    const resp = await axios.get(`${API}/api/brand/insights/export-pdf?range=${range}`, {
      withCredentials: true,
      responseType: 'blob',
    });
    const blob = new Blob([resp.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wave-insights-${range}-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    console.error('PDF export failed', e);
    // eslint-disable-next-line no-alert
    alert('Could not generate PDF report — please retry in a moment.');
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════
export default function InsightsAnalyticsPage() {
  const [range, setRange] = useState('30d');
  const [tab, setTab] = useState('performance');
  const { profile, isDemo } = useDemoProfile();

  // Use brand-specific waves + insights for demo brands; otherwise fall back
  // to the legacy wellness fixtures so non-demo accounts behave unchanged.
  const WAVE_METRICS = (isDemo && profile?.wave_metrics?.length)
    ? profile.wave_metrics
    : WAVE_METRICS_FALLBACK;
  const DEEP_DIVE_INSIGHTS = useMemo(() => {
    const src = (isDemo && profile?.deep_dive_insights?.length)
      ? profile.deep_dive_insights
      : DEEP_DIVE_INSIGHTS_FALLBACK;
    // Re-hydrate icon component refs (server sends icon name strings).
    return src.map(i => ({
      ...i,
      icon: typeof i.icon === 'string' ? (ICON_MAP[i.icon] || Sparkles) : i.icon,
      action: i.action || 'Apply recommendation',
      applyTo: i.applyTo || '/build-wave',
    }));
  }, [isDemo, profile]);

  // Filter by time range + precompute summary metrics.
  const filtered = useMemo(() => {
    const maxDays = RANGES.find(r => r.id === range)?.days || 30;
    return WAVE_METRICS.filter(w => w.startedDaysAgo <= maxDays);
  }, [range, WAVE_METRICS]);

  const hasLive = filtered.some(w => w.status === 'live');

  const summary = useMemo(() => {
    if (filtered.length === 0) {
      return { totalReach: 0, avgRoas: '0.0', totalCreators: 0, avgPulse: 0, avgEng: '0.0' };
    }
    const totalReach = filtered.reduce((s, w) => s + w.reachNum, 0);
    const avgRoas = (filtered.reduce((s, w) => s + w.roas, 0) / filtered.length).toFixed(1);
    const totalCreators = filtered.reduce((s, w) => s + w.creators, 0);
    const avgPulse = Math.round(filtered.reduce((s, w) => s + (w.wave_pulse ?? w.pulse ?? 0), 0) / filtered.length);
    const avgEng = (filtered.reduce((s, w) => s + w.engRate, 0) / filtered.length).toFixed(1);
    return { totalReach, avgRoas, totalCreators, avgPulse, avgEng };
  }, [filtered]);

  return (
    <DashboardLayout>
      <div className="p-5 lg:p-8 space-y-5 max-w-[1500px] mx-auto" data-testid="insights-analytics-page">

        {/* ═══ Header ═══ */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-black text-white flex items-center gap-2">
              Insights &amp; Analytics
              {hasLive && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-500/10 border border-green-500/25 text-green-400 text-[10px] font-bold ml-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> LIVE UPDATING
                </span>
              )}
            </h1>
            <p className="text-sm text-gray-500 mt-1">Wave Performance Intelligence — <span className="text-[#00D4FF]">patent pending</span></p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <RangePicker range={range} onChange={setRange} />
            <button onClick={() => exportInsightsPDF(range, profile)}
                    className="px-3 py-2 rounded-lg bg-[#181A20] border border-[#242730] hover:border-[#00D4FF]/30 text-xs text-gray-300 font-semibold flex items-center gap-1.5 transition-all"
                    data-testid="insights-export-btn">
              <Download className="h-3.5 w-3.5" />Export Report
            </button>
            <button className="px-3 py-2 rounded-lg bg-[#181A20] border border-[#242730] hover:border-[#FF4D94]/30 text-xs text-gray-300 font-semibold flex items-center gap-1.5 transition-all"
                    data-testid="insights-share-btn">
              <Share2 className="h-3.5 w-3.5" />Share
            </button>
          </div>
        </div>

        {/* Saved vs paid strip (iter 84 #6) — anchors the "we replace paid media" narrative */}
        <SavedVsPaidStrip
          totalReach={summary.totalReach}
          totalImpressions={filtered.reduce((s, w) => s + (typeof w.impressions === 'string' ? parseFloat(w.impressions) * 1e6 : 0), 0)}
          waveCount={filtered.length}
        />

        {/* ═══ Summary Cards (always visible) ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="summary-grid">
          <SummaryCard label="Avg Wave Pulse"  value={summary.avgPulse}                            icon={Activity}    color="#4ade80" metric="wave_pulse"
                       vsIndustry={pctDelta(summary.avgPulse, VERTICAL_BENCHMARKS.wave_pulse)} />
          <SummaryCard label="Reach · unique"  value={`${(summary.totalReach / 1e6).toFixed(1)}M`} icon={Eye}         color="#00D4FF" metric="reach"
                       vsIndustry={pctDelta(summary.totalReach / Math.max(filtered.length, 1), VERTICAL_BENCHMARKS.reach_per_creator * 100)} />
          <SummaryCard label="Avg ROAS"        value={`${summary.avgRoas}x`}                       icon={TrendingUp}  color="#4ade80" metric="roas"
                       vsIndustry={pctDelta(parseFloat(summary.avgRoas), VERTICAL_BENCHMARKS.roas)} />
          <SummaryCard label="Avg Engagement"  value={`${summary.avgEng}%`}                        icon={MessageCircle} color="#FF4D94" metric="engagement_rate"
                       vsIndustry={pctDelta(parseFloat(summary.avgEng), VERTICAL_BENCHMARKS.engagement_rate)} />
          <SummaryCard label="Active Creators" value={summary.totalCreators.toLocaleString()}      icon={Users}       color="#00D4FF" />
        </div>

        {/* ═══ Tabs ═══ */}
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="insights-tabs">
          {TABS.map(t => {
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                      data-testid={`insights-tab-${t.id}`}
                      className={`px-4 py-2 rounded-lg text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
                        active ? 'bg-[#00D4FF] text-black' : 'bg-[#181A20] border border-[#242730] text-gray-400 hover:border-[#00D4FF]/30 hover:text-white'}`}>
                <t.icon className="h-3.5 w-3.5" />{t.label}
                {t.id === 'intelligence' && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FF4D94] text-white text-[8px] font-black">AI</span>}
              </button>
            );
          })}
        </div>

        {/* ═══ Tab content ═══ */}
        {tab === 'performance'  && <PerformanceTab metrics={filtered} />}
        {tab === 'platform'     && <PlatformTab />}
        {tab === 'creators'     && <CreatorTiersTab tierCreators={isDemo ? profile?.tier_creators : null} />}
        {tab === 'audience'     && <AudienceTab metrics={filtered} whitespaceCohorts={isDemo ? profile?.whitespace_cohorts : null} />}
        {tab === 'intelligence' && <SmartIntelligenceTab summary={summary} metrics={filtered} deepDiveInsights={DEEP_DIVE_INSIGHTS} />}
        {tab === 'cohortrollup' && <CohortRollupTab />}
      </div>
    </DashboardLayout>
  );
}

function RangePicker({ range, onChange }) {
  return (
    <div className="flex items-center bg-[#181A20] border border-[#242730] rounded-lg p-0.5" data-testid="range-picker">
      {RANGES.map(r => {
        const active = range === r.id;
        return (
          <button key={r.id} onClick={() => onChange(r.id)}
                  data-testid={`range-${r.id}`}
                  className={`px-3 py-1.5 text-[11px] font-bold rounded-md transition-all flex items-center gap-1.5 ${
                    active ? 'bg-[#00D4FF] text-black' : 'text-gray-400 hover:text-white'}`}>
            {r.id === range && <Calendar className="h-3 w-3" />}
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, metric, vsIndustry }) {
  return (
    <div className="bg-[#181A20] border border-[#242730] rounded-xl p-4 hover:border-[#2E3240] transition-all">
      <Icon className="h-4 w-4 mb-2" style={{ color }} />
      <p className="text-xl font-black text-white">{value}</p>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5 flex items-center gap-0.5">
        {label}
        {metric && <MetricExplainerIcon metric={metric} size="xs" placement="top" />}
      </div>
      {vsIndustry !== null && vsIndustry !== undefined && Number.isFinite(vsIndustry) && (
        <div className="mt-1.5 pt-1.5 border-t border-[#242730]" data-testid={`summary-delta-${(label || '').toLowerCase().replace(/\s/g, '-')}`}>
          <DeltaPill delta={vsIndustry} label="vs industry" />
        </div>
      )}
    </div>
  );
}

// ═══ Tab: Performance ═══
function PerformanceTab({ metrics }) {
  const roasData = useMemo(() =>
    [...metrics].sort((a, b) => b.roas - a.roas).map(w => ({ label: w.wave.split(' ').slice(0, 2).join(' '), roas: w.roas })), [metrics]);
  const reachData = useMemo(() =>
    [...metrics].sort((a, b) => b.reachNum - a.reachNum).map(w => ({ label: w.wave.split(' ').slice(0, 2).join(' '), reach: w.reachNum })), [metrics]);

  // Sort by recency for wave-over-wave deltas (the table renders most-recent at top).
  const sortedByRecency = useMemo(() =>
    [...metrics].sort((a, b) => a.startedDaysAgo - b.startedDaysAgo), [metrics]);

  return (
    <div className="space-y-5" data-testid="performance-tab">
      {/* (4) Annotated time-series of Pulse / ROAS / Reach across waves */}
      <AnnotatedTimeSeries metrics={metrics} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard icon={TrendingUp} iconColor="#4ade80" title="ROAS by Wave">
          <SimpleBarChart data={roasData} valueKey="roas" labelKey="label" color="#4ade80" unitSuffix="x" />
        </ChartCard>
        <ChartCard icon={Eye} iconColor="#00D4FF" title="Reach by Wave">
          <SimpleBarChart data={reachData} valueKey="reach" labelKey="label" color="#00D4FF" />
        </ChartCard>
      </div>

      {/* (3) Project Re-Fire panel — converts completed waves into a planning surface */}
      <RefirePanel metrics={metrics} />

      <div className="bg-[#181A20] border border-[#242730] rounded-xl overflow-hidden">
        <div className="p-5 border-b border-[#242730]">
          <h3 className="font-bold text-white flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#00D4FF]" />Wave Performance Metrics</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="wave-metrics-table">
            <thead>
              <tr className="border-b border-[#242730]">
                {[
                  { label: 'Wave' }, { label: 'Status' },
                  { label: 'Wave Pulse', metric: 'wave_pulse' },
                  { label: 'Reach', metric: 'reach' },
                  { label: 'Eng Rate', metric: 'engagement_rate' },
                  { label: 'ROAS', metric: 'roas' },
                  { label: 'Sentiment' }, { label: 'Creators' },
                ].map(h => (
                  <th key={h.label} className="px-4 py-3 text-[9px] text-gray-600 uppercase tracking-wider font-semibold">
                    <span className="inline-flex items-center gap-0.5">
                      {h.label}
                      {h.metric && <MetricExplainerIcon metric={h.metric} size="xs" placement="bottom" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedByRecency.map((w, idx) => {
                // (1) Wave-over-wave: compare to the immediately-preceding wave by recency.
                const prior = sortedByRecency[idx + 1];
                const pulseVsPrior = prior ? pctDelta((w.wave_pulse ?? w.pulse ?? 0), (prior.wave_pulse ?? prior.pulse ?? 0)) : null;
                const roasVsPrior  = prior ? pctDelta(w.roas, prior.roas) : null;
                return (
                  <React.Fragment key={w.id}>
                    <tr onClick={() => { window.location.href = `/campaigns/${w.id}`; }}
                        className="border-b border-[#242730]/50 hover:bg-[#242730]/30 transition-colors cursor-pointer" data-testid={`wave-row-${w.id}`}>
                      <td className="px-4 py-3 text-sm font-semibold text-white max-w-[200px] truncate hover:text-[#00D4FF]">{w.wave}</td>
                      <td className="px-4 py-3">
                        <Badge className={`text-[9px] ${w.status === 'live' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                          {w.status === 'live' && <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse mr-1 inline-block" />}
                          {w.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Progress value={(w.wave_pulse ?? w.pulse ?? 0)} className="h-1.5 w-16 [&>div]:bg-[#4ade80]" />
                          <span className="text-xs text-white font-bold">{(w.wave_pulse ?? w.pulse ?? 0)}</span>
                          <DeltaPill delta={pulseVsPrior} label="vs prior" />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-white">{w.reach}</td>
                      <td className="px-4 py-3 text-sm text-white">{w.engRateLabel}</td>
                      <td className="px-4 py-3 text-sm font-bold text-green-400">
                        {w.roas}x <DeltaPill delta={roasVsPrior} label="vs prior" />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400">{w.sentiment}</td>
                      <td className="px-4 py-3 text-sm text-white">{w.creators.toLocaleString()}</td>
                    </tr>
                    {/* (8) Lifecycle scrubber — animated timeline replay per wave */}
                    <tr className="border-b border-[#242730]/30">
                      <td colSpan="8" className="px-4 pb-3" onClick={e => e.stopPropagation()}>
                        <LifecycleScrubber wave={w} />
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══ Tab: Platform ═══
function PlatformTab() {
  return (
    <div className="space-y-5" data-testid="platform-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard icon={Eye} iconColor="#00D4FF" title="Reach Share">
          <SimpleBarChart data={[...PLATFORM_BREAKDOWN].sort((a, b) => b.reachShare - a.reachShare)} valueKey="reachShare" labelKey="platform" color="#00D4FF" unitSuffix="%" />
        </ChartCard>
        <ChartCard icon={MessageCircle} iconColor="#FF4D94" title="Engagement Share">
          <SimpleBarChart data={[...PLATFORM_BREAKDOWN].sort((a, b) => b.engShare - a.engShare)} valueKey="engShare" labelKey="platform" color="#FF4D94" unitSuffix="%" />
        </ChartCard>
        <ChartCard icon={TrendingUp} iconColor="#4ade80" title="Conversion Share">
          <SimpleBarChart data={[...PLATFORM_BREAKDOWN].sort((a, b) => b.conversionShare - a.conversionShare)} valueKey="conversionShare" labelKey="platform" color="#4ade80" unitSuffix="%" />
        </ChartCard>
      </div>
      <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5">
        <h3 className="font-bold text-white mb-1">Cross-platform insight</h3>
        <p className="text-xs text-gray-400 leading-relaxed">
          TikTok delivers <span className="text-white font-bold">48%</span> of your reach, but only
          <span className="text-white font-bold"> 22%</span> of your conversions. Instagram is the opposite — it
          delivers <span className="text-white font-bold">31%</span> of reach and
          <span className="text-white font-bold"> 54%</span> of conversions. The Wave Orchestrator will
          automatically tilt creative toward Instagram's hook-format for conversion-goal waves
          unless you override it in the Plan Wave wizard.
        </p>
      </div>
    </div>
  );
}

// ═══ Tab: Creator Tiers ═══
function CreatorTiersTab({ tierCreators }) {
  const [drillTier, setDrillTier] = useState(null);  // iter 84 #5: click-to-drill
  const roasByTier = useMemo(() =>
    [...CREATOR_TIERS].sort((a, b) => b.roas - a.roas).map(t => ({ label: t.tier, roas: t.roas })), []);
  return (
    <div className="space-y-5" data-testid="creator-tiers-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard icon={TrendingUp} iconColor="#4ade80" title="ROAS by creator tier">
          <SimpleBarChart data={roasByTier} valueKey="roas" labelKey="label" color="#4ade80" unitSuffix="x" />
        </ChartCard>
        <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5 space-y-3">
          <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-4"><Users className="h-4 w-4 text-[#FF4D94]" />Cohort composition</h3>
          {CREATOR_TIERS.map(t => (
            <button key={t.tier} onClick={() => setDrillTier(t)}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A] border border-[#242730] hover:border-[#00D4FF]/30 transition-colors cursor-pointer text-left"
                    data-testid={`tier-drill-${t.tier.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
              <div>
                <p className="text-xs font-semibold text-white flex items-center gap-1.5">
                  {t.tier}
                  <ChevronRight className="h-3 w-3 text-gray-600" />
                </p>
                <p className="text-[10px] text-gray-500">~${t.avgCost.toLocaleString()} avg CPM ·  {t.creatorCount} in cohort · click to view roster</p>
              </div>
              <span className={`text-sm font-black ${t.roas >= 7 ? 'text-green-400' : t.roas >= 5 ? 'text-[#00D4FF]' : 'text-amber-400'}`}>{t.roas}x</span>
            </button>
          ))}
        </div>
      </div>
      <CreatorDrillModal tier={drillTier} onClose={() => setDrillTier(null)} tierCreators={tierCreators} />
    </div>
  );
}

// ═══ Tab: Audience signal (sentiment) ═══
function AudienceTab({ metrics, whitespaceCohorts }) {
  return (
    <div className="space-y-5" data-testid="audience-tab">
      {/* (2) Whitespace cohorts — front-loaded so it's the first thing investors see */}
      <WhitespaceCohortsCard cohorts={whitespaceCohorts} />

      <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5">
        <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-4"><MessageCircle className="h-4 w-4 text-[#FF4D94]" />Sentiment by wave</h3>
        <div className="space-y-3">
          {metrics.map(w => (
            <div key={w.id} className="flex items-center gap-3">
              <span className="text-[11px] text-gray-300 w-44 truncate font-medium">{w.wave}</span>
              <div className="flex-1 h-5 bg-[#0A0A0A] rounded overflow-hidden relative">
                <div className="h-full rounded transition-all duration-700"
                     style={{
                       width: `${w.sentimentScore}%`,
                       background: `linear-gradient(90deg, rgba(255,77,148,0.2), #FF4D94, #00D4FF)`,
                     }} />
                <span className="absolute right-2 top-0.5 text-[10px] font-black text-white">{w.sentiment} · {w.sentimentScore}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══ Tab: Smart Intelligence ═══
function SmartIntelligenceTab({ summary, metrics, deepDiveInsights = [] }) {
  const DEEP_DIVE_INSIGHTS = deepDiveInsights;
  // Predict the next wave's ROAS by averaging the top-3 performers
  const top = [...metrics].sort((a, b) => b.roas - a.roas).slice(0, 3);
  const forecast = top.length ? (top.reduce((s, w) => s + w.roas, 0) / top.length).toFixed(1) : '—';

  return (
    <div className="space-y-5" data-testid="intelligence-tab">
      {/* Forecast card */}
      <div className="rounded-2xl p-5 relative overflow-hidden"
           style={{
             background: 'linear-gradient(135deg, rgba(0,212,255,0.08), rgba(255,77,148,0.06), #121212)',
             border: '1px solid rgba(0,212,255,0.3)',
           }}>
        <div className="absolute -top-16 -right-14 w-48 h-48 rounded-full blur-3xl opacity-30" style={{ background: '#00D4FF' }} />
        <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#00D4FF]/15 flex items-center justify-center" style={{ boxShadow: '0 0 20px rgba(0,212,255,0.3)' }}>
              <Brain className="h-6 w-6 text-[#00D4FF]" />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest text-gray-400 font-bold">Predicted next-wave performance</p>
              <p className="text-sm text-white font-semibold mt-0.5">Based on your top-3 historical waves</p>
            </div>
          </div>
          <div>
            <div className="flex items-baseline gap-6">
              <div>
                <p className="text-3xl font-black text-[#00D4FF]">{forecast}x</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Projected ROAS</p>
              </div>
              <div>
                <p className="text-3xl font-black text-[#FF4D94]">{(summary.avgPulse + 4)}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Projected Pulse</p>
              </div>
              <div>
                <p className="text-3xl font-black text-white">{Math.round(summary.totalReach / metrics.length / 1e6 * 1.15)}M+</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Projected Reach</p>
              </div>
            </div>
          </div>
          <Link to="/build-wave?apply=duplicate" data-testid="apply-forecast-btn"
             className="px-5 py-3 rounded-lg bg-[#FF4D94] text-white font-bold text-xs flex items-center gap-1.5 hover:brightness-110 hover:scale-[1.02] transition-all"
             style={{ boxShadow: '0 0 20px rgba(255,77,148,0.35)' }}>
            Plan next wave <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {/* Deep-dive insights */}
      <div className="bg-[#181A20] border border-[#242730] rounded-xl p-6">
        <h3 className="font-bold text-white flex items-center gap-2 mb-4"><Award className="h-4 w-4 text-[#FF4D94]" />Deep-Dive Recommendations</h3>
        <div className="space-y-3" data-testid="deep-dive-insights">
          {DEEP_DIVE_INSIGHTS.map(insight => {
            const chip = PRIORITY_CHIP[insight.priority];
            const Icon = insight.icon;
            return (
              <div key={insight.text} className="flex items-start gap-3 p-4 bg-[#0A0A0A] rounded-xl border border-[#242730] hover:border-[#00D4FF]/25 transition-all group">
                <div className="w-8 h-8 rounded-lg bg-[#FF4D94]/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="h-4 w-4 text-[#FF4D94]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start gap-2 mb-1 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[8px] font-black" style={{ background: chip.bg, color: chip.fg }}>{chip.label}</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">{insight.text}</p>
                </div>
                <Link to={insight.applyTo} data-testid={`apply-insight-${insight.applyTo.split('=')[1]}`}
                      className="text-[10px] text-[#00D4FF] font-bold hover:underline flex items-center gap-1 whitespace-nowrap">
                  {insight.action} <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ icon: Icon, iconColor, title, children }) {
  return (
    <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5">
      <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4" style={{ color: iconColor }} />{title}
      </h3>
      {children}
    </div>
  );
}



// ═══ Tab: Cohort Patterns — multi-wave rollup (iter 78) ═══
// Reads GET /api/brand/cohort-intelligence/rollup and surfaces the
// patterns observed across all of the brand's recent waves so brands
// move from per-wave decisions to portfolio strategy.
function CohortRollupTab() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/brand/cohort-intelligence/rollup?days=90`, { withCredentials: true });
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (!data || data.waves_analyzed === 0) {
    return (
      <div className="bg-[#181A20] border border-[#242730] rounded-xl p-8 text-center" data-testid="cohort-rollup-empty">
        <Activity className="h-8 w-8 mx-auto text-gray-600 mb-3" />
        <p className="text-sm text-white font-bold">No waves analyzed in the last 90 days</p>
        <p className="text-xs text-gray-500 mt-1">Launch a wave to populate this view.</p>
      </div>
    );
  }

  const factorRows = [
    { key: 'density',          label: 'Density',          desc: 'Are creators firing on time?' },
    { key: 'cross_engagement', label: 'Cross-engagement', desc: 'Are creators replying to each other?' },
    { key: 'platform_signal',  label: 'Platform signal',  desc: 'Is engagement spread across platforms?' },
    { key: 'time_compression', label: 'Time compression', desc: 'How tight is the sync window?' },
  ];
  const leverLabels = {
    creator_swap: 'Swap for Bench Creator',
    reply_storm:  'Run Reply Storm',
    spawn_burst:  'Spawn Reinforcement Burst',
    shift_time:   'Tighten Sync Window',
  };

  return (
    <div className="space-y-4" data-testid="cohort-rollup-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-[#181A20] border border-[#242730] rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Waves analyzed</p>
          <p className="text-3xl font-black text-white tabular-nums">{data.waves_analyzed}</p>
          <p className="text-[10px] text-gray-500 mt-1">last {data.window_days} days</p>
        </div>
        <div className="bg-[#181A20] border border-[#242730] rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Average Wave Pulse</p>
          <p className="text-3xl font-black text-[#4ade80] tabular-nums">{data.avg_pulse}</p>
          <p className="text-[10px] text-gray-500 mt-1">across all waves</p>
        </div>
        <div className="bg-[#181A20] border border-[#242730] rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1">Top recommended lever</p>
          <p className="text-sm font-black text-white mt-1">
            {data.top_recommended_levers[0]
              ? leverLabels[data.top_recommended_levers[0].lever_id] || data.top_recommended_levers[0].lever_id
              : '—'}
          </p>
          <p className="text-[10px] text-gray-500 mt-1">
            {data.top_recommended_levers[0]
              ? `Recommended in ${data.top_recommended_levers[0].recommended_in_waves} of ${data.waves_analyzed} waves`
              : ''}
          </p>
        </div>
      </div>

      <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5">
        <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-1">
          <Brain className="h-4 w-4 text-[#FF4D94]" />
          Pulse factor gaps · 30-day average
        </h3>
        <p className="text-[11px] text-gray-500 mb-4">Where you have the most headroom. Bigger bar = bigger opportunity.</p>
        <div className="space-y-3">
          {factorRows.map(f => {
            const gap = data.avg_factor_gaps[f.key] || 0;
            const color = gap < 10 ? '#4ade80' : gap < 25 ? '#FFB020' : '#FF4D6B';
            return (
              <div key={f.key} className="flex items-center gap-3" data-testid={`factor-row-${f.key}`}>
                <div className="w-32 flex-shrink-0">
                  <p className="text-xs font-bold text-white">{f.label}</p>
                  <p className="text-[10px] text-gray-500">{f.desc}</p>
                </div>
                <div className="flex-1 bg-[#0a0a0a] rounded-full h-2 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${Math.min(100, gap)}%`, background: color, transition: 'width 600ms ease' }} />
                </div>
                <span className="text-sm font-black tabular-nums w-12 text-right" style={{ color }}>{gap.toFixed(0)}%</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-[#181A20] border border-[#242730] rounded-xl p-5">
        <h3 className="font-bold text-white text-sm flex items-center gap-2 mb-1">
          <Sparkles className="h-4 w-4 text-[#00D4FF]" />
          Patterns across your waves
        </h3>
        <p className="text-[11px] text-gray-500 mb-4">If a lever shows up here repeatedly, plan for it before the next wave.</p>
        <div className="space-y-2">
          {data.top_recommended_levers.map(l => (
            <div key={l.lever_id} className="flex items-center justify-between bg-[#0a0a0a] rounded-lg p-3" data-testid={`recommended-lever-${l.lever_id}`}>
              <span className="text-sm text-white font-bold">{leverLabels[l.lever_id] || l.lever_id}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#00D4FF] font-black">
                Recommended in {l.recommended_in_waves} / {data.waves_analyzed} waves
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
